import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Save, X, AlertTriangle } from 'lucide-react';

interface EmergencyContact {
  id: string;
  user_id?: string;
  name: string;
  phone: string;
  email: string;
  relationship: string;
  is_primary: boolean;
}

export function EmergencyContactsManager() {
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newContact, setNewContact] = useState<Partial<EmergencyContact>>({
    name: '',
    phone: '',
    email: '',
    relationship: '',
    is_primary: false,
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      // Try to fetch from database first
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from('emergency_contacts')
          .select('*')
          .eq('user_id', user.id)
          .order('is_primary', { ascending: false })
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (data) {
          setContacts(data);
          return;
        }
      }

      // Fallback to localStorage for non-authenticated users
      const stored = localStorage.getItem('emergencyContacts');
      if (stored) {
        try {
          setContacts(JSON.parse(stored));
        } catch {
          setContacts([]);
        }
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Fallback to localStorage on error
      const stored = localStorage.getItem('emergencyContacts');
      if (stored) {
        try {
          setContacts(JSON.parse(stored));
        } catch {
          setContacts([]);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const saveContactsToStorage = (updatedContacts: EmergencyContact[]) => {
    localStorage.setItem('emergencyContacts', JSON.stringify(updatedContacts));
    setContacts(updatedContacts);
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone) {
      toast.error('Name and phone are required');
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const contactData = {
        user_id: user?.id,
        name: newContact.name,
        phone: newContact.phone,
        email: newContact.email || '',
        relationship: newContact.relationship || 'Emergency Contact',
        is_primary: newContact.is_primary || false,
      };

      if (user) {
        // Save to database
        const { data, error } = await supabase
          .from('emergency_contacts')
          .insert([contactData])
          .select()
          .single();

        if (error) throw error;
        if (data) {
          setContacts(prev => [data, ...prev]);
          toast.success('Contact saved to database');
        }
      } else {
        // Save to localStorage
        const contact: EmergencyContact = {
          ...contactData,
          id: Date.now().toString(),
        };
        const updated = [...contacts, contact];
        saveContactsToStorage(updated);
        toast.success('Contact saved locally');
      }

      setNewContact({ name: '', phone: '', email: '', relationship: '', is_primary: false });
      setIsEditing(false);
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (id: string) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { error } = await supabase
          .from('emergency_contacts')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) throw error;
        toast.success('Contact deleted');
      } else {
        const updated = contacts.filter(c => c.id !== id);
        saveContactsToStorage(updated);
        toast.success('Contact removed');
      }

      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimary = async (id: string) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // First, unset all primaries
        await supabase
          .from('emergency_contacts')
          .update({ is_primary: false })
          .eq('user_id', user.id);

        // Then set the selected one
        const { error } = await supabase
          .from('emergency_contacts')
          .update({ is_primary: true })
          .eq('id', id)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        const updated = contacts.map(c => ({
          ...c,
          is_primary: c.id === id,
        }));
        saveContactsToStorage(updated);
      }

      setContacts(prev => prev.map(c => ({
        ...c,
        is_primary: c.id === id,
      })));
      toast.success('Primary contact updated');
    } catch (error) {
      console.error('Error setting primary:', error);
      toast.error('Failed to update primary contact');
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = async () => {
    if (!confirm('Are you sure you want to remove all emergency contacts?')) {
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        const { error } = await supabase
          .from('emergency_contacts')
          .delete()
          .eq('user_id', user.id);

        if (error) throw error;
      }

      saveContactsToStorage([]);
      toast.success('All contacts removed');
    } catch (error) {
      console.error('Error clearing contacts:', error);
      toast.error('Failed to clear contacts');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          <h3 className="font-black text-slate-900 uppercase">Emergency Contacts</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsEditing(!isEditing)}
          className="flex items-center gap-2"
          disabled={saving}
        >
          {isEditing ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {isEditing ? 'Cancel' : 'Add Contact'}
        </Button>
      </div>

      {isEditing && (
        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[8px] font-black text-slate-500 uppercase mb-1">Name *</label>
              <input
                type="text"
                placeholder="Full name"
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-bold"
              />
            </div>
            <div>
              <label className="block text-[8px] font-black text-slate-500 uppercase mb-1">Phone *</label>
              <input
                type="tel"
                placeholder="0400 000 000"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-bold"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[8px] font-black text-slate-500 uppercase mb-1">Email (for alerts)</label>
              <input
                type="email"
                placeholder="email@example.com"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-bold"
              />
            </div>
            <div>
              <label className="block text-[8px] font-black text-slate-500 uppercase mb-1">Relationship</label>
              <input
                type="text"
                placeholder="e.g., Spouse, Parent"
                value={newContact.relationship}
                onChange={(e) => setNewContact({ ...newContact, relationship: e.target.value })}
                className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-bold"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPrimary"
              checked={newContact.is_primary || false}
              onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
              className="w-4 h-4"
            />
            <label htmlFor="isPrimary" className="text-sm font-bold text-slate-700">Primary contact</label>
          </div>
          <Button 
            onClick={handleAddContact} 
            className="w-full" 
            size="sm"
            disabled={saving || !newContact.name || !newContact.phone}
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Contact'}
          </Button>
        </div>
      )}

      {contacts.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="font-bold text-sm">No emergency contacts added</p>
          <p className="text-xs mt-1">Add contacts who will be alerted in case of emergency</p>
        </div>
      ) : (
        <div className="space-y-2">
          {contacts.map((contact) => (
            <div
              key={contact.id}
              className={`p-3 rounded-xl border flex items-center justify-between ${
                contact.is_primary
                  ? 'bg-purple-50 border-purple-200'
                  : 'bg-white border-slate-200'
              }`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-slate-900">{contact.name}</p>
                  {contact.is_primary && (
                    <span className="text-[10px] font-black uppercase bg-purple-600 text-white px-2 py-0.5 rounded-full">
                      Primary
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600">{contact.relationship}</p>
                <div className="flex gap-3 mt-1">
                  <a href={`tel:${contact.phone}`} className="text-xs text-purple-600 hover:underline font-bold">
                    📞 {contact.phone}
                  </a>
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="text-xs text-purple-600 hover:underline font-bold">
                      ✉️ {contact.email}
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!contact.is_primary && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSetPrimary(contact.id)}
                    className="text-xs font-bold"
                    disabled={saving}
                  >
                    Set Primary
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteContact(contact.id)}
                  className="text-red-600 hover:text-red-700"
                  disabled={saving}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {contacts.length > 0 && (
        <div className="pt-3 border-t">
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearAll}
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 font-bold"
            disabled={saving}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Remove All Contacts
          </Button>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs font-bold text-amber-800">
            These contacts will be notified via email when you trigger the SOS button. 
            Make sure to include at least one email address for alerts.
          </p>
        </div>
      </div>
    </div>
  );
}
